import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { ReactComponent as SearchIcon } from "assets/img/search_01.svg";
import errorSvg from "assets/img/error-red.svg";
import { Card } from "components/Card";
import Form from "components/Form";
import Link from "components/Link";
import Loading from "components/Loading";
import NearbyStoresGoogleMap from "components/NearbyStoresGoogleMap";
import { Body } from "components/Typography";
import Constants from "helpers/constants";
import {
  collectNearbyPostalCodesClient,
  dedupePostalStrings,
  reverseGeocodeWithMapsKey,
} from "helpers/reverseGeocodeClient";
import { getItemJson, setItem } from "helpers/storage";
import { UserLocation } from "interfaces/location";
import { Store } from "interfaces/store";
import { State } from "state";
import { createAnalyticsEvent, EventTypes, Status } from "state/analytics";
import { getLatLongFromZip, getNearbyZips, getUserZipCode, setUserLocationZip } from "state/location";
import { fetchUserStores } from "state/stores";
import { InputAdornment, TextField } from "@material-ui/core";
import { LightThemeTableWithPagination } from "components/LightThemeTableWithPagination";
import config from "config";
import { sortRowsByStoreNumber } from "utils/sorters";

interface NearbyStoreOwnProps extends RouteComponentProps {}
type NearbyStoresProps = NearbyStoreOwnProps & PropsFromRedux;

const DEFAULT_PAGE_SIZE = 10;

/**
 * Matches GET /api/locations/nearby-zips `count` (max 50 on the server).
 * Flow → GET /api/stores/me?zip=…
 * - Single zip: `?zip=M5V`
 * - Radius / multi: `?zip=M5V,M5T,M4K,...` (client postal + up to 50 unique codes from nearby-zips, comma-separated)
 */
const NEARBY_ZIP_SAMPLE_COUNT = 60;
/** Initial geolocation uses a 6-mile radius only (matches product expectation for “nearby”). */
const NEARBY_RADIUS_MILES = 6;

interface NearbyStoresState {
  tableData: Store[];
  zipCode: string;
  mapLoaded: boolean;
  geolocationError?: number;
  latitude?: number;
  longitude?: number;
  /** 1-indexed current page shown in the table UI */
  currentPage: number;
  pageSize: number;
  /** Total pages returned by the server (pageCount from API response) */
  totalPages: number;
  /** Active sort field, empty string means no sort */
  sortField: string;
  sortOrder: "asc" | "desc";
  /**
   * Zip codes within the ~6-mile radius of the user's location (from getNearbyZips / client sampling).
   * Undefined when the user performed a manual zip search (uses single-zip filter instead).
   */
  nearbyZips?: string[];
  /**
   * True while we are waiting for the browser geolocation API to respond.
   * During this window neither zip nor nearby-zips are known, so we must not
   * call the stores API (which would return ALL stores on an empty zip).
   */
  isLocating: boolean;
  /** True when zip-codes + nearby-zips returned nothing — user must search by zip */
  noPostalFromLocation?: boolean;
}

const defaultState: NearbyStoresState = {
  tableData: [],
  zipCode: "",
  mapLoaded: false,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 1,
  sortField: "",
  sortOrder: "asc",
  nearbyZips: undefined,
  isLocating: false,
  noPostalFromLocation: false,
};

const InputFieldV2: React.FC<{
  label?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  dataQa?: string;
  onChange: (e: any) => void;
  error?: string;
  maxLength?: number;
}> = ({ label, name, value, placeholder, dataQa, onChange, error, maxLength }) => (
  <div>
    <TextField
      placeholder="Search By Zip Code"
      variant="standard"
      fullWidth
      value={value || ""}
      onChange={onChange}
      inputProps={{ maxLength }}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment style={{ marginRight: 8, marginLeft: 0 }} position="start">
            <div>
              <SearchIcon style={{ margin: "-3px" }} />
            </div>
          </InputAdornment>
        ),
        style: {
          padding: "12px 15px",
          border: "1.5px solid #E5B611",
          borderRadius: 5,
          fontStyle: "italic",
          fontSize: 14,
          backgroundColor: "rgb(229, 182, 17, 0.05)",
          height: 26,
          boxSizing: "border-box",
        },
      }}
    />
  </div>
);

class NearbyStores extends React.Component<NearbyStoresProps, NearbyStoresState> {
  public state = defaultState;

  public componentDidMount() {
    const cachedData = getItemJson("nearbyStoresData") as
      | { zipCode?: string; latitude?: number; longitude?: number; nearbyZips?: string[] }
      | undefined;

    // Use != null so lat/lng 0 (valid) does not skip the cache branch (bug: `0 && x` is falsy).
    const hasCachedCoords =
      cachedData != null &&
      typeof cachedData.latitude === "number" &&
      typeof cachedData.longitude === "number" &&
      !Number.isNaN(cachedData.latitude) &&
      !Number.isNaN(cachedData.longitude);

    if (hasCachedCoords) {
      // ── CACHE PATH ─────────────────────────────────────────────────────────
      const { latitude, longitude } = cachedData as {
        latitude: number;
        longitude: number;
        zipCode?: string;
        nearbyZips?: string[];
      };
      const cachedZip = cachedData.zipCode || "";
      const cachedNearbyZips = cachedData.nearbyZips;
      // Only skip /nearby-zips + client sampling when we already cached a multi-zip radius list.
      // Old saves had zip + coords only (no nearbyZips) or a single entry — re-run resolve to fill.
      const hasMultiZipCache = !!(cachedNearbyZips && cachedNearbyZips.length > 1);
      const hasZip = !!cachedZip;

      // Commit map + zip state first, then call /api/stores/me so fetchPageData sees nearbyZips in state
      // (needed for pagination/sort) and we never read stale pre-commit state.
      this.setState(
        {
          latitude,
          longitude,
          mapLoaded: true,
          nearbyZips: cachedNearbyZips,
          zipCode: cachedZip,
          isLocating: !hasMultiZipCache && !hasZip,
        },
        () => {
          if (hasMultiZipCache) {
            this.fetchPageData(cachedZip, 1, this.state.pageSize, latitude, longitude, cachedNearbyZips);
          } else {
            // No multi-zip list in session (common after manual search or older builds) — recompute
            // zip-codes + nearby-zips + client ring so /api/stores/me gets [zip,...nearby].
            this.setState({ isLocating: true });
            this.resolveLocationAndFetch(latitude, longitude);
          }
        }
      );
    } else if ("geolocation" in navigator) {
      // ── GEOLOCATION PATH ───────────────────────────────────────────────────
      this.setState({ isLocating: true });
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          this.resolveLocationAndFetch(latitude, longitude);
        },
        (e) => {
          if (e.code === 1) {
          this.setState({ geolocationError: e.code, isLocating: false, noPostalFromLocation: true  });
          } else {
            this.setState({ isLocating: false, noPostalFromLocation: true }, () => {
              this.fetchPageData("", 1, this.state.pageSize);
            });
          }
        },
        { enableHighAccuracy: false }
      );
    }
  }

  /**
   * 1) GET /api/locations/zip-codes + GET /api/locations/nearby-zips (6 mi, count=50)
   * 2) If we still have fewer than 2 distinct postals, union with in-browser 6 mi sampling (same geometry)
   * 3) If still no postal: reverse-geocode center only and merge/dedupe
   * 4) GET /api/stores/me — `zip` = dedupe([zip,...nearby]).join(",") whenever we have that list
   */
  private resolveLocationAndFetch = (latitude: number, longitude: number) => {
    const pageSize = this.state.pageSize;
    const coords = { latitude, longitude };

    Promise.all([
      this.props.getUserZipCode(coords),
      this.props.getNearbyZips(coords, NEARBY_RADIUS_MILES, NEARBY_ZIP_SAMPLE_COUNT),
    ])
      .then(async ([zip, nearbyFromServer]) => {
        let nearbyZips = dedupePostalStrings([...nearbyFromServer]);

        if (nearbyZips.length < 2) {
          const clientCollected = await collectNearbyPostalCodesClient(latitude, longitude, {
            radiusMiles: NEARBY_RADIUS_MILES,
            sampleCount: NEARBY_ZIP_SAMPLE_COUNT,
          });
          nearbyZips = dedupePostalStrings([...nearbyZips, ...clientCollected]);
        }

        let merged = dedupePostalStrings([...(zip ? [zip] : []), ...nearbyZips]);
        let effectiveZip = merged[0] || "";

        if (!effectiveZip && !merged.length) {
          const clientZip = await reverseGeocodeWithMapsKey(latitude, longitude);
          if (clientZip) {
            merged = dedupePostalStrings([clientZip, ...nearbyZips]);
            effectiveZip = merged[0] ?? clientZip;
          }
        }

        // Full list for /api/stores/me: [center zip, ...nearby] (deduped). Always pass this array
        // (even when length 1) so fetchPageData can join(",") and never drop nearby.
        const storesMeZips = merged.length > 0 ? merged : undefined;

        const noPostal = !effectiveZip && !storesMeZips?.length;

        this.setState(
          {
            zipCode: effectiveZip,
            nearbyZips: storesMeZips,
            isLocating: false,
            noPostalFromLocation: noPostal,
          },
          () => {
            if (effectiveZip && !zip) {
              this.props.setUserLocationZip(effectiveZip, { latitude, longitude });
            }
            this.fetchPageData(effectiveZip, 1, pageSize, latitude, longitude, storesMeZips);
            if (effectiveZip) {
              this.saveLocationToCache(effectiveZip, latitude, longitude, storesMeZips);
            }
          }
        );
      })
      .catch(() => {
        this.setState({ isLocating: false, noPostalFromLocation: true }, () => {
          this.fetchPageData("", 1, pageSize, latitude, longitude);
        });
      });
  };

  /**
   * Fetch a single page from the server.
   * `page` is 1-indexed (UI); we subtract 1 for the 0-indexed server param.
   * Sort is read from the current component state (set by handleSortChange).
   * If lat/lng are provided the map will also be updated and the location
   * will be persisted to localStorage.
   *
   * When `nearbyZips` is supplied (or already in state) the query uses the
   * comma-separated zip list for radius search. When the user
   * performs a manual zip search `nearbyZips` in state is cleared, so the
   * paging path falls back to the single-zip filter automatically.
   */
  private fetchPageData = (
    zip: string,
    page: number,
    size: number,
    latitude?: number,
    longitude?: number,
    nearbyZips?: string[]
  ) => {
    const serverPage = page - 1;
    const { sortField, sortOrder } = this.state;
    const sort = sortField ? `${sortField}.${sortOrder}` : undefined;

    // Prefer the explicitly-passed list ([zip, ...nearby] from geolocation), then state.
    // Manual zip search clears `nearbyZips`, so we only have `zip` (single value).
    const fromCaller = nearbyZips ?? this.state.nearbyZips;
    const trimmedZip = zip.trim();
    const hasAnyZip = !!trimmedZip || !!(fromCaller && fromCaller.length > 0);

    // If no zip is provided, fetch all stores as a fallback
    if (!hasAnyZip) {
      const storeQuery = { zip: "", size, page: serverPage, ...(sort ? { sort } : {}) };
      this.props
        .fetchUserStores(storeQuery)
        .then((payload) => {
          const stores = payload?.data ?? [];
          const storesPageCount = payload?.pageCount ?? 1;
          this.setState({ tableData: stores, currentPage: page, pageSize: size, totalPages: storesPageCount, mapLoaded: !!(latitude && longitude)});
        })
        .catch(() => {});
      return;
    }

    // Geolocation: zip param is always dedupe([zip,...nearby]).join(",") so every code is sent.
    const zipParam =
      fromCaller && fromCaller.length > 0
        ? dedupePostalStrings(fromCaller).join(",")
        : trimmedZip;
    const storeQuery = { zip: zipParam, size, page: serverPage, ...(sort ? { sort } : {}) };

    this.props
      .fetchUserStores(storeQuery)
      .then((payload) => {
        const stores = payload?.data ?? [];
        const storesPageCount = payload?.pageCount ?? 1;

        const update: Partial<NearbyStoresState> = {
          tableData: stores,
          currentPage: page,
          pageSize: size,
          totalPages: storesPageCount,
        };

        if (latitude !== undefined && longitude !== undefined) {
          update.latitude = latitude;
          update.longitude = longitude;
          const zipForCache =
            zip.trim() || (fromCaller && fromCaller.length > 0 ? fromCaller[0] : "");
          this.saveLocationToCache(zipForCache, latitude, longitude, fromCaller);
        }
        update.mapLoaded = true;

        this.setState(update as NearbyStoresState);
      })
      .catch(() => {});
  };

  /**
   * Save location coordinates (and optional nearby zip list) to localStorage.
   * Table data is never cached — the table always fetches a fresh page from
   * the server. Caching nearbyZips avoids a redundant /nearby-zips API call
   * on subsequent page loads when the user is in the same area.
   */
  public saveLocationToCache = (
    zipCode: string,
    latitude: number,
    longitude: number,
    nearbyZips?: string[]
  ) => {
    setItem("nearbyStoresData", { zipCode, latitude, longitude, nearbyZips });
  };

  public renderErrorScreen(errorMessage: any) {
    const myStoresLink = (
      <Link className="body-link" to="/stores/me">
        My Stores
      </Link>
    );
    return (
      <Card>
        <Card.Header
          style={{
            padding: "6px",
            border: "1.5px solid #E5B611",
            borderRadius: "5px",
            marginLeft: "15px",
            marginRight: "15px",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "12px",
            height: "100%",
            backgroundColor: "rgb(229, 182, 17, 0.05)",
          }}
        >
          <img src={errorSvg} alt="Error Icon" data-qa="error-icon" style={{ height: "26px" }} />
          <Body type={"1"} data-qa="error-text">
            {/* {errorMessage} You can enter a zip code below or visit {myStoresLink} to search for
            stores near you. */}
            {errorMessage} You can enter a zip code below to search for
            stores near you.
          </Body>
        </Card.Header>
      </Card>
    );
  }

  public getLocationFromZip = (zip: string) => {
    // Never search with an empty zip — it would return every store in the DB.
    if (!zip || !zip.trim()) return;

    const normalizedZip = zip.replace(/\s+/g, " ").trim();

    // Manual zip search — clear nearbyZips so paging stays in single-zip mode
    // and clear stale rows while keeping the map visible during the new search.
    this.setState({
      tableData: [],
      currentPage: 1,
      zipCode: normalizedZip,
      nearbyZips: undefined,
      noPostalFromLocation: false,
    });

    const { sortField, sortOrder } = this.state;
    const sort = sortField ? `${sortField}.${sortOrder}` : undefined;

    this.props
      .getLatLongFromZip(zip)
      .then((location) => {
        const hasLatLong = location?.payload?.latitude && location?.payload?.longitude;

        // Single fetchUserStores call — avoids the race condition that would
        // occur if fetchPageData and a separate analytics call both dispatched
        // fetchUserStores with the same params simultaneously.
        this.props
          .fetchUserStores({
            zip: normalizedZip,
            size: this.state.pageSize,
            page: 0,
            ...(sort ? { sort } : {}),
          })
          .then((payload) => {
          const stores = payload?.data ?? [];
          const storesPageCount = payload?.pageCount ?? 1;

          const update: Partial<NearbyStoresState> = {
            tableData: stores,
            currentPage: 1,
            totalPages: storesPageCount,
          };

          if (hasLatLong) {
            // New coordinates found — move the map to the searched zip
            update.latitude = location.payload.latitude;
            update.longitude = location.payload.longitude;
            update.mapLoaded = true;
            this.saveLocationToCache(normalizedZip, location.payload.latitude, location.payload.longitude);
          } else {
            // No coordinates for this zip — keep the map visible with whatever
            // coordinates we already have rather than hiding it entirely
            update.mapLoaded = !!(this.state.latitude || this.state.longitude);
          }

          this.setState(update as NearbyStoresState);

          this.props.createAnalyticsEvent({
            event: EventTypes.NEARBY_STORES_SEARCH,
            eventType: "Action",
            nearbyStoresSearchStatus: hasLatLong ? Status.SUCCESS : Status.FAILURE,
            nearbyStoresSearchQuery: normalizedZip,
            searchResults: stores.length,
          });
        });
      })
      .catch(() => {
        this.props
          .fetchUserStores({
            zip: normalizedZip,
            size: this.state.pageSize,
            page: 0,
            ...(sort ? { sort } : {}),
          })
          .then((payload) => {
          const stores = payload?.data ?? [];
          const storesPageCount = payload?.pageCount ?? 1;
          this.setState({
            tableData: stores,
            totalPages: storesPageCount,
            // Keep the map visible with existing coordinates on geocoding error
            mapLoaded: !!(this.state.latitude || this.state.longitude),
          });
          this.props.createAnalyticsEvent({
            event: EventTypes.NEARBY_STORES_SEARCH,
            eventType: "Action",
            nearbyStoresSearchStatus: Status.FAILURE,
            nearbyStoresSearchQuery: normalizedZip,
            searchResults: stores.length,
          });
        });
      });
  };

  public handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault();
    const zip = this.state.zipCode.replace(/\s+/g, " ").trim();
    if (zip.length <= 4) return; // at least 5 chars (US ZIP or postal code)
    this.getLocationFromZip(zip);
  };

  /** Called by LightThemeTableWithPagination when the user navigates pages */
  public handlePageChange = (page: number, pageSize: number) => {
    this.setState({ currentPage: page, pageSize, tableData: [] }, () => {
      const { zipCode, nearbyZips, latitude, longitude } = this.state;
      this.fetchPageData(zipCode, page, pageSize, latitude, longitude, nearbyZips);
    });
  };

  /** Called by LightThemeTableWithPagination when the user clicks a column header */
  public handleSortChange = (field: string, order: "asc" | "desc") => {
    const size = this.state.pageSize;
    this.setState({ sortField: field, sortOrder: order, currentPage: 1, tableData: [] }, () => {
      const { zipCode, nearbyZips, latitude, longitude } = this.state;
      this.fetchPageData(zipCode, 1, size, latitude, longitude, nearbyZips);
    });
  };

  public render() {
    const {
      hasError,
      isLoadingLocation,
      isLoadingStores,
      currentZip = "",
      userCoords,
    } = this.props;
    const {
      geolocationError,
      latitude,
      longitude,
      zipCode,
      tableData,
      sortField,
      sortOrder,
      currentPage,
      pageSize,
      totalPages,
      isLocating,
      noPostalFromLocation,
    } = this.state;

    const displayTableData =
      sortField === "retail_store_num"
        ? sortRowsByStoreNumber(tableData, (row) => row.retail_store_num, sortOrder)
        : tableData;

    const mapLat = latitude ?? userCoords?.latitude;
    const mapLng = longitude ?? userCoords?.longitude;

    let errorMessage: string | undefined;
    if (geolocationError) {
      errorMessage =
        geolocationError === 1
          ? "Geolocation is disabled in your browser."
          : "We were unable to find your location.";
    } else if (hasError) {
      errorMessage = "We were unable to find your location.";
    } else if (noPostalFromLocation) {
      errorMessage =
        "We couldn't determine a postal or ZIP code from your position (location services may be misconfigured). ";
    }

    const handleRowClick = (row: any) => {
      this.props.history.push(`/projects/${row.project_id}/stores/${row.id}`);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const zip = e.target.value;
      // US ZIP (digits) or Canadian/UK-style postal codes (letters, digits, spaces)
      if (zip === "" || /^[A-Za-z0-9\s-]+$/.test(zip)) {
        this.setState({ zipCode: zip });
      }
    };

    return (
      <div
        style={{
          gap: 0,
          borderRadius: "16px",
          padding: "5px",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <Card style={styles.container}>
          {/* {config.GOOGLE_MAPS_API_KEY}g */}
          <Form onSubmit={this.handleSubmit} style={{ width: "100%", padding: "15px" }}>
            <div style={styles.inputRow}>
              <InputFieldV2
                name="address"
                label="Address"
                placeholder="Search By Zipcode"
                data-qa="address-field"
                value={zipCode}
                maxLength={12}
                onChange={handleChange}
              />
              <button
                type="submit"
                data-qa="submit-announcement"
                disabled={zipCode.trim().length <= 4}
                style={{
                  ...styles.sendButton,
                  opacity: zipCode.trim().length > 4 ? 1 : 0.5,
                  cursor: zipCode.trim().length > 4 ? "pointer" : "not-allowed",
                  backgroundColor: "#E5B611",
                }}
              >
                Search
              </button>
            </div>
          </Form>

          {errorMessage && this.renderErrorScreen(errorMessage)}

          {/* Spinner while the browser resolves geolocation (isLocating) or while
              the zip-code reverse-geocode API call is in flight (isLoadingLocation).
              Store page fetches must NOT hide the map — the map handles its own updates. */}
          {(isLocating || isLoadingLocation) && <Loading />}
            
          {!isLocating && !isLoadingLocation && this.state.mapLoaded && mapLat != null && mapLng != null && (
            <NearbyStoresGoogleMap
              currentZip={currentZip}
              position={{ lat: mapLat, lng: mapLng }}
              stores={displayTableData}
            />
          )}

          <div style={styles.tableContainer}>
            <LightThemeTableWithPagination
              data={displayTableData}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              pageSizeOptions={[10]}
              isLoading={isLoadingStores}
              onPageChange={this.handlePageChange}
              onSortChange={this.handleSortChange}
              columns={[
                {
                  field: "retail_store_num",
                  header: "Store No.",
                  formatter: (_: any, row: any) => (
                    <div onClick={() => handleRowClick(row)}>{row.retail_store_num}</div>
                  ),
                },
                {
                  field: "name",
                  header: "Store Name",
                  formatter: (_: any, row: any) => (
                    <div onClick={() => handleRowClick(row)}>{row.name}</div>
                  ),
                },
                {
                  field: "store_address",
                  header: "Address",
                  formatter: (_: any, row: any) => (
                    <div onClick={() => handleRowClick(row)}>{row.store_address}</div>
                  ),
                },
                {
                  field: "chain_name",
                  header: "Chain Name",
                  formatter: (_: any, row: any) => (
                    <div onClick={() => handleRowClick(row)}>{row.chain_name}</div>
                  ),
                },
                {
                  field: "project_name",
                  header: "Project Name",
                  formatter: (_: any, row: any) => (
                    <div onClick={() => handleRowClick(row)}>{row.project_name}</div>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    currentZip: state.location.content && state.location.content.zip,
    userCoords: state.location.content?.currentCoordinates,
    stores: state.stores.content?.data,
    storesPageCount: state.stores.content?.pageCount,
    hasError: state.location.error,
    isLoadingLocation: state.location.loading,
    isLoadingStores: state.stores.loading,
    isMobile: state.breakpoint.content === Constants.BREAKPOINT_MOBILE,
  }),
  {
    createAnalyticsEvent,
    getUserZipCode,
    getNearbyZips,
    getLatLongFromZip,
    setUserLocationZip,
    fetchUserStores,
  }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default withRouter(connecter(NearbyStores));

const styles = {
  container: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 16,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  tableContainer: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 16,
    width: "100%",
    boxSizing: "border-box" as const,
  },
  sendButton: {
    backgroundColor: "#E5B611",
    color: "#000000",
    border: "none",
    padding: "0px 12px",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 16,
    whiteSpace: "nowrap",
    fontWeight: 400,
    height: "26px",
  },
  inputRow: { display: "flex", gap: 4 },
} as const;
